'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CreditCard,
  Download,
  ImageIcon,
  Loader2,
  Sparkles,
  ZoomIn,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { Link } from '@/core/i18n/navigation';
import { AIMediaType, AITaskStatus } from '@/extensions/ai/types';
import {
  ImageUploader,
  ImageUploaderValue,
  LazyImage,
} from '@/shared/blocks/common';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Label } from '@/shared/components/ui/label';
import { Progress } from '@/shared/components/ui/progress';
import { useAppContext } from '@/shared/contexts/app';
import { trackPhotoEffects } from '@/shared/lib/analytics';

interface ImageUpscalerProps {
  maxSizeMB?: number;
  srOnlyTitle?: string;
}

interface GeneratedImage {
  id: string;
  url: string;
}

interface BackendTask {
  id: string;
  status: string;
  taskInfo: string | null;
}

const COST_CREDITS = 1;
const POLL_INTERVAL = 5000; // 5 seconds
const GENERATION_TIMEOUT = 180000; // 3 minutes

const MODEL = 'recraft-ai/recraft-crisp-upscale';
const PROVIDER = 'replicate';

const parseTaskResult = (taskInfo: string | null) => {
  if (!taskInfo) return null;
  try {
    return JSON.parse(taskInfo);
  } catch {
    return null;
  }
};

const extractImageUrls = (result: any): string[] => {
  if (!result) {
    return [];
  }

  const output = result.output ?? result.images ?? result.data;

  if (!output) {
    return [];
  }

  if (typeof output === 'string') {
    return [output];
  }

  if (Array.isArray(output)) {
    return output
      .flatMap((item) => {
        if (!item) return [];
        if (typeof item === 'string') return [item];
        if (typeof item === 'object') {
          const candidate =
            item.url ?? item.uri ?? item.image ?? item.src ?? item.imageUrl;
          return typeof candidate === 'string' ? [candidate] : [];
        }
        return [];
      })
      .filter(Boolean);
  }

  if (typeof output === 'object') {
    const candidate =
      output.url ?? output.uri ?? output.image ?? output.src ?? output.imageUrl;
    if (typeof candidate === 'string') {
      return [candidate];
    }
  }

  return [];
};

export function ImageUpscaler({
  maxSizeMB = 24,
  srOnlyTitle,
}: ImageUpscalerProps) {
  const t = useTranslations('photo-effects.image-upscaler');

  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(
    null
  );
  const [taskStatus, setTaskStatus] = useState<AITaskStatus | null>(null);
  const [downloadingImageId, setDownloadingImageId] = useState<string | null>(null);

  // Ref to track if success toast has been shown for current task
  const successToastShownRef = useRef<string | null>(null);

  const { user, isCheckSign, setIsShowSignModal, fetchUserCredits } =
    useAppContext();

  const remainingCredits = user?.credits?.remainingCredits ?? 0;

  const handleImagesChange = useCallback(
    (items: ImageUploaderValue[]) => {
      const uploadedUrls = items
        .filter((item) => item.status === 'uploaded' && item.url)
        .map((item) => item.url as string);
      setImageUrls(uploadedUrls);
    },
    []
  );

  const resetTaskState = useCallback(() => {
    setIsGenerating(false);
    setProgress(0);
    setTaskId(null);
    setGenerationStartTime(null);
    setTaskStatus(null);
  }, []);

  const handleGenerate = async () => {
    if (imageUrls.length === 0) {
      toast.error(t('errors.no-image'));
      return;
    }

    if (!user) {
      setIsShowSignModal(true);
      return;
    }

    if (remainingCredits < COST_CREDITS) {
      toast.error(t('errors.insufficient-credits'));
      return;
    }

    trackPhotoEffects({
      effect: 'image-upscale-start',
      credits_used: COST_CREDITS,
    });

    setIsGenerating(true);
    setProgress(0);
    setTaskStatus(null);
    setGenerationStartTime(Date.now());
    successToastShownRef.current = null; // Reset toast flag

    try {
      const response = await fetch('/api/ai/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mediaType: AIMediaType.IMAGE,
          scene: 'image-upscale',
          provider: PROVIDER,
          model: MODEL,
          prompt: 'upscale image', // Required by API validation
          options: {
            image: imageUrls[0],
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to start upscaling');
      }

      const { code, message, data } = await response.json();
      if (code !== 0) {
        throw new Error(message || 'Failed to start upscaling');
      }

      const newTaskId = data?.id;
      if (!newTaskId) {
        throw new Error('Task id missing in response');
      }

      // Check if task completed immediately
      if (data.status === AITaskStatus.SUCCESS && data.taskInfo) {
        const parsedResult = parseTaskResult(data.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (imageUrls.length > 0) {
          const firstImageUrl = imageUrls[0];
          const timestamp = Date.now();
          const newImageId = `${newTaskId}-${timestamp}`;

          const newImage: GeneratedImage = {
            id: newImageId,
            url: firstImageUrl,
          };

          setGeneratedImages([newImage]);
          toast.success(t('success.upscaled'));

          trackPhotoEffects({
            effect: 'image-upscale',
            credits_used: COST_CREDITS,
          });

          setProgress(100);
          resetTaskState();
          await fetchUserCredits();
          return;
        }
      }

      setTaskId(newTaskId);
      setProgress(25);
      await fetchUserCredits();
    } catch (error: any) {
      console.error('Image upscale error:', error);
      toast.error(error.message || t('errors.generation-failed'));
      resetTaskState();
    }
  };

  const pollTaskStatus = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        if (
          generationStartTime &&
          Date.now() - generationStartTime > GENERATION_TIMEOUT
        ) {
          toast.error(t('errors.generation-timeout'));
          resetTaskState();
          return true;
        }

        const response = await fetch('/api/ai/query', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ taskId: id }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch task status');
        }

        const { code, message, data } = await response.json();
        if (code !== 0) {
          throw new Error(message || 'Query task failed');
        }

        const task = data as BackendTask;
        const currentStatus = task.status as AITaskStatus;
        setTaskStatus(currentStatus);

        const parsedResult = parseTaskResult(task.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (currentStatus === AITaskStatus.PENDING) {
          setProgress((prev) => Math.max(prev, 20));
          return false;
        }

        if (currentStatus === AITaskStatus.PROCESSING) {
          if (imageUrls.length > 0) {
            setProgress((prev) => Math.max(prev, 85));
          } else {
            setProgress((prev) => Math.min(prev + 10, 80));
          }
          return false;
        }

        if (currentStatus === AITaskStatus.SUCCESS) {
          if (imageUrls.length === 0) {
            toast.error('No image generated');
          } else {
            const firstImageUrl = imageUrls[0];
            const timestamp = Date.now();
            const newImageId = `${id}-${timestamp}`;

            const newImage: GeneratedImage = {
              id: newImageId,
              url: firstImageUrl,
            };

            setGeneratedImages([newImage]);

            // Only show toast once per task
            if (successToastShownRef.current !== id) {
              successToastShownRef.current = id;
              toast.success(t('success.upscaled'));
            }

            trackPhotoEffects({
              effect: 'image-upscale',
              credits_used: COST_CREDITS,
            });
          }

          setProgress(100);
          resetTaskState();
          await fetchUserCredits();
          return true;
        }

        if (currentStatus === AITaskStatus.FAILED) {
          const errorMessage = parsedResult?.errorMessage || t('errors.generation-failed');
          toast.error(errorMessage);
          resetTaskState();
          fetchUserCredits();
          return true;
        }

        setProgress((prev) => Math.min(prev + 5, 95));
        return false;
      } catch (error: any) {
        console.error('Error polling task status:', error);
        toast.error(`Query task failed: ${error.message}`);
        resetTaskState();
        fetchUserCredits();
        return true;
      }
    },
    [generationStartTime, resetTaskState, t, fetchUserCredits]
  );

  useEffect(() => {
    if (!taskId || !isGenerating) {
      return;
    }

    let cancelled = false;

    const tick = async () => {
      if (!taskId) {
        return;
      }
      const completed = await pollTaskStatus(taskId);
      if (completed) {
        cancelled = true;
      }
    };

    tick();

    const interval = setInterval(async () => {
      if (cancelled || !taskId) {
        clearInterval(interval);
        return;
      }
      const completed = await pollTaskStatus(taskId);
      if (completed) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [taskId, isGenerating, pollTaskStatus]);

  const handleDownload = useCallback(
    async (imageUrl: string, imageId: string) => {
      if (!imageUrl) {
        return;
      }

      try {
        setDownloadingImageId(imageId);
        // Fetch image via proxy to avoid CORS issues
        const response = await fetch(
          `/api/proxy/file?url=${encodeURIComponent(imageUrl)}`
        );
        if (!response.ok) {
          throw new Error('Failed to fetch image');
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `upscaled-${imageId}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
        toast.success(t('success.downloaded'));
      } catch (error) {
        console.error('Download error:', error);
        toast.error(t('errors.download-failed'));
      } finally {
        setDownloadingImageId(null);
      }
    },
    [t]
  );

  return (
    <div className="container mx-auto px-4 py-8">
      {srOnlyTitle && <h1 className="sr-only">{srOnlyTitle}</h1>}

      <div className="grid gap-8 lg:grid-cols-2">
        {/* Left Column - Upload & Controls */}
        <div className="space-y-6">
          {/* Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ZoomIn className="h-5 w-5" />
                {t('info.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {t('info.description')}
              </p>
            </CardContent>
          </Card>

          {/* Upload Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                {t('upload.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('upload.hint')}</Label>
                <ImageUploader
                  allowMultiple={false}
                  maxImages={1}
                  maxSizeMB={maxSizeMB}
                  onChange={handleImagesChange}
                />
                <p className="text-xs text-muted-foreground">
                  {t('upload.format')}
                </p>
              </div>

              {/* Credits Info */}
              <div className="rounded-lg border bg-muted/50 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      {t('credits.cost', { count: COST_CREDITS })}
                    </span>
                  </div>
                  {user && (
                    <span className="text-sm text-muted-foreground">
                      {t('credits.remaining', { count: remainingCredits })}
                    </span>
                  )}
                </div>
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || imageUrls.length === 0}
                className="w-full"
                size="lg"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('buttons.upscaling')}
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    {t('buttons.upscale')}
                  </>
                )}
              </Button>

              {/* Progress */}
              {isGenerating && (
                <div className="space-y-2">
                  <Progress value={progress} className="h-2" />
                  <p className="text-center text-xs text-muted-foreground">
                    {progress}% {taskStatus && `· ${taskStatus}`}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Results */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>
                {generatedImages.length > 0
                  ? t('results.title-upscaled')
                  : t('results.title-preview')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {generatedImages.length > 0 ? (
                <div className="space-y-4">
                  {generatedImages.map((image) => (
                    <div key={image.id} className="space-y-3">
                      <div className="relative overflow-hidden rounded-lg border">
                        <LazyImage
                          src={image.url}
                          alt="Upscaled image"
                          className="h-auto w-full"
                        />
                        <div className="absolute right-2 bottom-2 flex justify-end text-sm">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="ml-auto"
                            onClick={() => handleDownload(image.url, image.id)}
                            disabled={downloadingImageId === image.id}
                          >
                            {downloadingImageId === image.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex h-[400px] flex-col items-center justify-center rounded-lg border-2 border-dashed">
                  <ZoomIn className="mb-4 h-12 w-12 text-muted-foreground" />
                  <p className="text-center text-sm text-muted-foreground">
                    {t('results.success-description')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
